<template>
    <div v-if="range.length > 0">
        <p class="bsdc-config-filters__label">
            {{ label }}

            <Tooltip :text="tooltip" />

            <slot name="label" />
        </p>

        <div class="bsdc-config-selector__range">
            <Slider
                v-model="localRange"
                :min="min"
                :max="max"
                :step="step"
                :tooltips="true"
                :format="{
                    prefix: tooltipPrefix,
                    decimals: 2,
                }"
                show-tooltip="drag"
                @update="emit('update:range', localRange)"
            />

            <div class="bsdc:flex bsdc:justify-between bsdc:mt-3">
                <div class="bsdc:relative bsdc:w-[75px]">
                    <label
                        for="min-range"
                        class="bsdc:pointer-events-none bsdc:text-typography-body/50 bsdc:text-[9px] bsdc:leading-none bsdc:absolute bsdc:top-1.5 bsdc:left-0 bsdc:w-full bsdc:text-center"
                    >
                        min
                        {{ tooltipPrefix }}
                    </label>

                    <input
                        id="min-range"
                        type="number"
                        class="input"
                        :value="localRange[0]"
                        :min="min"
                        :max="max"
                        @input="handleMinInput"
                        @change="handleMinChange"
                    />
                </div>

                <div class="bsdc:relative bsdc:w-[75px]">
                    <label
                        for="max-range"
                        class="bsdc:pointer-events-none bsdc:text-typography-body/50 bsdc:text-[9px] bsdc:leading-none bsdc:absolute bsdc:top-1.5 bsdc:left-0 bsdc:w-full bsdc:text-center"
                    >
                        max
                        {{ tooltipPrefix }}
                    </label>

                    <input
                        id="max-range"
                        type="number"
                        class="input"
                        :value="localRange[1]"
                        :min="min"
                        :max="max"
                        @input="handleMaxInput"
                        @change="handleMaxChange"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, watch } from 'vue';

    import Slider from '@vueform/slider';

    import { debounce } from "@js/helpers";

    import Tooltip from "@js/Components/Tooltip.vue";

    const props = defineProps({
        label: String,
        name: String,
        tooltip: String,
        tooltipPrefix: String,
        range: Array,
        min: Number,
        max: Number,
        step: Number,
    });

    const emit = defineEmits(['update:range']);

    const localRange = ref(props.range);

    watch(() => props.range, (val) => {
        localRange.value = val;
    });

    const debouncedEmit = debounce((range) => {
        emit('update:range', range);
    }, 500);

    function handleMinInput(event) {
        const minValue = parseFloat(event.target.min);
        const maxValue = parseFloat(event.target.max);
        let value = parseFloat(event.target.value);

        if (isNaN(value)) {
            return;
        }

        if (value < minValue) {
            value = minValue;
        }

        if (value > maxValue) {
            value = maxValue;
        }

        if (value > localRange.value[1]) {
            value = localRange.value[1];
        }

        localRange.value[0] = value;
        debouncedEmit([...localRange.value]);
    }

    function handleMinChange(event) {
        handleMinInput(event);
        debouncedEmit.cancel();
        emit('update:range', [...localRange.value]);
    }

    function handleMaxInput(event) {
        const minValue = parseFloat(event.target.min);
        const maxValue = parseFloat(event.target.max);
        let value = parseFloat(event.target.value);

        if (isNaN(value)) {
            return;
        }

        if (value < minValue) {
            value = minValue;
        }

        if (value > maxValue) {
            value = maxValue;
        }

        if (value < localRange.value[0]) {
            value = localRange.value[0];
        }

        localRange.value[1] = value;
        debouncedEmit([...localRange.value]);
    }

    function handleMaxChange(event) {
        handleMaxInput(event);
        debouncedEmit.cancel();
        emit('update:range', [...localRange.value]);
    }
</script>

<style>
    @reference "../../css/app.css";

    @import "@vueform/slider/themes/default.css";

    .bsdc-config-selector__range {
        --slider-handle-width: 27px;
        --slider-handle-height: 20px;

        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        input[type=number] {
            -moz-appearance: textfield;
        }

        input {
            @apply bsdc:h-[40px] bsdc:min-h-auto bsdc:text-center bsdc:text-[13px] bsdc:pb-0 bsdc:pt-[12px];
        }

        .slider-target {
            @apply bsdc:px-3;
        }

        .slider-state-drag {
            @apply bsdc:z-30;
        }

        .slider-connects {
            @apply bsdc:bg-2-1;
        }

        .slider-connect {
            @apply bsdc:bg-neutral-900;
        }

        .slider-handle {
            @apply bsdc:shadow-none bsdc:border bsdc:border-neutral-900;
            @apply bsdc:bg-[image:var(--icon-drag)] bsdc:bg-no-repeat bsdc:bg-center bsdc:bg-[length:7px];
        }

        .slider-tooltip {
            @apply bsdc:bg-neutral-900 bsdc:border-none;

            &::before {
                @apply bsdc:border-t-neutral-900;
            }
        }
    }
</style>
